import React, { type FC, type ReactNode, useEffect, useRef, useState, type PropsWithChildren } from 'react';

import clsx from 'clsx';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { theme, variant } from '@lucid/theme';
// eslint-disable-next-line no-restricted-imports
import { Link as GatsbyLink, type GatsbyLinkProps as LinkGatsbyProps } from 'gatsby';
import gsap from 'gsap';

import type { Lucid } from '../../types/types';
import Icon, { type IconTypes } from '../icon/icon';
import { type BaseLinkOptions } from './link';

export interface StyledGatsbyLinkProps
  extends BaseLinkOptions,
    Omit<LinkGatsbyProps<unknown>, 'dangerouslySetInnerHTML'> {
  /** The children to be rendered. */
  children?: ReactNode;
  /** The link used in the Link component. */
  to: string;
  /** The available variants for the Link component. */
  variant?: Lucid.Theme.Component.Link['Variant'];
  /** Determines whether to render an icon */
  hasIcon?: boolean;
  /** The color of the Arrow or Carot icon */
  iconColor?: string;
  /** The type of icon to be rendered */
  iconType?: IconTypes;
  /** Splits last word from label to pair last word with arrow */
  updatedLabels?: string[];
}

const StyledGatsbyLinkBase = styled(GatsbyLink, {
  shouldForwardProp: isPropValid,
})<StyledGatsbyLinkProps>(
  variant({
    scale: 'link',
    variants: {
      defaultVariant: {},
    },
  }),
  ({ underline }) => [
    {
      label: 'gatsbyLink',
      '&[aria-disabled="true"]': {
        opacity: 0.5,
        pointerEvents: 'none',
        cursor: 'default',
      },
    },
    underline === 'always' && {
      textDecoration: 'underline',
    },
    underline === 'always' ||
      (underline === 'hover' && {
        '&:hover, &:active, &:focus': {
          textDecoration: 'underline',
        },
      }),
  ],
);

const StyledGatsbyLink: FC<PropsWithChildren<StyledGatsbyLinkProps>> = ({
  activeClassName,
  activeStyle,
  className,
  children,
  iconAnimation = 'default',
  hasIcon,
  iconColor,
  iconType,
  onClick,
  partiallyActive,
  replace,
  state,
  to,
  updatedLabels,
  ref,
  variant = 'primary',
  ...rest
}) => {
  const animationRef = useRef<HTMLAnchorElement & GatsbyLink<unknown>>(null);
  const q = gsap.utils.selector(animationRef);
  const [hasHover, setHasHover] = useState(false);

  useEffect(() => {
    if (animationRef.current && q('span').length) {
      hasHover
        ? gsap
            .timeline({ repeat: -1, repeatDelay: 0.7 })
            .to(q('span'), {
              x: iconAnimation === 'navigational' ? '0.6em' : iconAnimation === 'reverseNavigational' ? '-0.6em' : '',
              y: ['jumpLink', 'expand'].includes(iconAnimation)
                ? '0.6em'
                : ['collapse', 'jumplinkInverse'].includes(iconAnimation)
                  ? '-0.6em'
                  : '',
              duration: 0.5,
              ease: theme.beziers.slowStartEase,
              overwrite: true,
            })
            .to(q('span'), {
              x: '0em',
              y: '0em',
              duration: 0.5,
              ease: theme.beziers.fastEndEase,
            })
        : gsap.to(q('span'), {
            x: '0em',
            y: '0em',
            duration: 0.5,
            ease: theme.beziers.fastEndEase,
            overwrite: true,
          });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHover, hasIcon, iconAnimation, iconType]);

  return (
    <StyledGatsbyLinkBase
      {...rest}
      activeClassName={activeClassName}
      // eslint-disable-next-line react/jsx-sort-props
      activeStyle={activeStyle}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      data-link-as-gatsby="true"
      hasIcon={hasIcon}
      iconAnimation={iconAnimation}
      onClick={onClick}
      onMouseEnter={() => setHasHover(true)}
      onMouseLeave={() => setHasHover(false)}
      partiallyActive={partiallyActive}
      replace={replace}
      state={state}
      to={to}
      variant={variant}
    >
      {hasIcon ? (
        <span
          ref={animationRef}
          css={[iconAnimation === 'reverseNavigational' && { display: 'inline-block', direction: 'rtl' }]}
        >
          {updatedLabels ? `${updatedLabels[0]} ` : children}
          <span css={{ whiteSpace: 'nowrap' }}>
            {updatedLabels ? updatedLabels[1] : ''}
            <span
              css={[
                {
                  label: 'hasIcon',
                  position: 'relative',
                  fontSize: '0.75em',
                  marginLeft: '0.5em',
                  display: 'inline-block',
                  color: 'inherit',
                },
                ['navigational', 'reverseNavigational'].includes(iconAnimation) && {
                  display: 'inline-flex',
                  fontSize: '0.9em',
                  alignItems: 'flex-end',
                  '& span': {
                    height: 'unset',
                  },
                },
                iconAnimation === 'reverseNavigational' && {
                  marginLeft: 0,
                  marginRight: '.5em',
                },
              ]}
            >
              <Icon
                color={iconColor}
                css={[
                  iconAnimation === 'jumpLink' && {
                    // !important prevents transform property being overriden by animation
                    // this seems to only be an issue in Storybook, but it feels safer to leave it
                    transform: 'rotate(90deg) !important',
                  },
                  ['collapse', 'reverseNavigational'].includes(iconAnimation) && {
                    transform: 'rotate(180deg) !important',
                  },
                ]}
                // eslint-disable-next-line react/jsx-sort-props
                className={clsx(
                  ['expand', 'collapse'].includes(iconAnimation) && 'hasCarotIcon',
                  ['navigational', 'jumpLink', 'reverseNavigational'].includes(iconAnimation) && 'hasArrowIcon',
                )}
                type={iconType!}
              />
            </span>
          </span>
        </span>
      ) : (
        children
      )}
    </StyledGatsbyLinkBase>
  );
};

export { StyledGatsbyLink };
export default StyledGatsbyLink;
